'use client';

import { useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { FeedLinks, StreamPlayer } from '@/components/streaming/StreamPlayer';
import { platformName } from '@/components/streaming/streamPlatforms';
import { Chip, ChipRow } from '@/components/ui/Chip';
import { safeWatchUrl } from '@/app/[locale]/event/[eventId]/libs/streamLinks';
import { dayKeyInZone } from '@/lib/dayKey';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import type { CourtDefinition } from '@/app/[locale]/admin/event-management/[eventId]/edit/scheduler/match-scheduler-types';
import {
  feedsForCourtDay,
  feedStatus,
  scheduleSlots,
  shownDay,
  type CourtFeed,
} from '@/app/[locale]/event/[eventId]/libs/eventFeeds';
import type { EventFeeds } from '@/app/[locale]/event/[eventId]/libs/eventFeedsLoad';
import { formatDayKey } from '@/app/[locale]/event/[eventId]/libs/matchesDay';
import { applyScheduleScore } from '@/app/[locale]/event/[eventId]/libs/liveScoreViews';
import {
  defaultRow,
  panelRow,
  playStart,
  watchRows,
  type WatchRow,
} from '@/app/[locale]/event/[eventId]/libs/watchMatches';
import { courtLabel, type EventVenue } from '@/app/[locale]/event/[eventId]/libs/venues';
import type { PublicMatch } from '@/app/[locale]/event/[eventId]/[slug]/(tabs)/schedule/libs/scheduleStatus';
import { DayChipLabel } from '@/app/[locale]/event/[eventId]/[slug]/(tabs)/schedule/components/DayChipLabel';
import { useEventLiveScores } from '@/app/[locale]/event/[eventId]/[slug]/components/useEventLiveScores';
import { WatchMatchList } from './WatchMatchList';
import { WatchScorecardPanel } from './WatchScorecardPanel';

const TICK_MS = 30_000;

/** The header says Live while the playing thing is live, and nothing otherwise. */
function LiveBadge() {
  const t = useTranslations('EventWatch');
  return (
    <span className="bg-danger text-danger-foreground rounded-full px-2 py-0.5 text-xs font-medium">
      {t('status.live')}
    </span>
  );
}

/** A YouTube watch link that opens at `offsetSec`; anything else is returned as given. */
export function seekUrl(url: string, offsetSec: number): string {
  if (offsetSec <= 0) return url;
  try {
    const u = new URL(url);
    if (!/(^|\.)youtube\.com$|^youtu\.be$/.test(u.hostname)) return url;
    u.searchParams.set('t', `${Math.floor(offsetSec)}s`);
    return u.toString();
  } catch {
    return url;
  }
}

// A pick is a schedule row id; null follows the court's default row, '' means "no row" (a camera was chosen).
type Pick = string | null;

export function WatchClient({
  eventId,
  nowIso,
  feeds,
  matches,
  scheduleFailed = false,
  day,
  venues,
  courts,
  initialCourtId,
  initialMatch = null,
}: {
  eventId: string;
  /** The server's clock: both renders of the first paint use it, so they agree. */
  nowIso: string;
  feeds: EventFeeds;
  /** The day's schedule rows (names already resolved and alias-gated on the server). */
  matches: PublicMatch[];
  /** The schedule read failed: the list says so rather than looking like an empty day. */
  scheduleFailed?: boolean;
  /** The event-zone day key the rows belong to. */
  day: string;
  venues: EventVenue[];
  courts: CourtDefinition[];
  initialCourtId: string | null;
  /** `?match=`: a schedule row id to select on load, as a tap would. An unknown id, or a row a tap could not select (`none`), is ignored. */
  initialMatch?: string | null;
}) {
  const t = useTranslations('EventWatch');
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { byScheduleId } = useEventLiveScores(eventId);
  const [now, setNow] = useState(() => Date.parse(nowIso));
  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const zone = feeds.timeZone;
  const todayKey = dayKeyInZone(new Date(now).toISOString(), zone);
  const dayKey = feeds.days.includes(day) ? day : shownDay(feeds.days, todayKey);
  const ctx = useMemo(() => ({ venues, courts }), [venues, courts]);

  // A match the live feed says is under way counts as in progress before the schedule refreshes.
  const liveMatches = useMemo(
    () =>
      matches.map((m) => {
        const applied = applyScheduleScore(m, byScheduleId[m.id]);
        return applied === m ? m : { ...applied, matchId: applied.matchId ?? m.matchId };
      }),
    [matches, byScheduleId],
  );
  const slots = useMemo(() => scheduleSlots(liveMatches), [liveMatches]);

  const courtFeeds = (courtId: string) => feedsForCourtDay(feeds.feeds, courtId, dayKey);
  const statusOf = (feed: CourtFeed) => feedStatus(feed, dayKey, todayKey, slots, now, zone);

  const defaultCourtId = () => {
    if (initialCourtId && courts.some((c) => c.id === initialCourtId)) return initialCourtId;
    const withFeeds = courts.filter((c) => courtFeeds(c.id).length > 0);
    return (
      withFeeds.find((c) => courtFeeds(c.id).some((f) => statusOf(f) === 'live'))?.id ??
      withFeeds[0]?.id ??
      courts[0]?.id ??
      null
    );
  };
  // `?match=` seeds the same state a tap writes, under the key the first render computes (props
  // only, so the server and the client agree); it needs a court, since the link always carries one.
  const seeded =
    initialMatch && initialCourtId ? { court: initialCourtId, id: initialMatch } : null;
  const [pickedCourt, setPickedCourt] = useState<string | null>(() => seeded?.court ?? null);
  const [picks, setPicks] = useState<Record<string, Pick>>(() =>
    seeded ? { [`${seeded.court}|${dayKey}`]: seeded.id } : {},
  );
  const [held, setHeld] = useState<Record<string, { feedId: string; startSec: number }>>({});
  const [cameras, setCameras] = useState<Record<string, string>>({});
  const courtId =
    pickedCourt && courts.some((c) => c.id === pickedCourt) ? pickedCourt : defaultCourtId();
  const key = `${courtId ?? ''}|${dayKey}`;

  const selectedFeeds = courtId ? courtFeeds(courtId) : [];
  const inProgressIds = useMemo(
    () => new Set(liveMatches.filter((m) => m.status === 'live').map((m) => m.id)),
    [liveMatches],
  );
  const rows = useMemo(
    () =>
      watchRows(
        liveMatches.filter((m) => m.courtId === courtId),
        selectedFeeds,
        feeds.locators,
        inProgressIds,
      ),
    // selectedFeeds is derived from feeds.feeds, courtId and dayKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveMatches, courtId, dayKey, feeds.feeds, feeds.locators, inProgressIds],
  );

  const pick = picks[key] ?? null;
  const firstFeedLive = selectedFeeds[0] ? statusOf(selectedFeeds[0]) === 'live' : false;
  const selectedRow: WatchRow<PublicMatch> | null =
    pick === ''
      ? null
      : ((pick ? rows.find((r) => r.match.id === pick && r.kind !== 'none') : null) ??
        defaultRow(rows, firstFeedLive));

  // The panel follows the selection, and on a live court with none keeps the match that just ended.
  // A viewer who chose a camera (`pick === ''`) gets no panel; the player never reads this row.
  const shownRow = panelRow(selectedRow, rows, firstFeedLive, pick === '', (m) =>
    m.status === 'completed' && !isWinnerlessRow({ outcome: m.outcome, is_walkover: m.isWalkover }),
  );

  // Pasted links are the only real cameras; sessions never are.
  const linkFeeds = selectedFeeds.filter((f) => f.source === 'link');
  const cameraFeed = selectedFeeds.find((f) => f.id === cameras[key]) ?? selectedFeeds[0];
  // A row's feed is looked up among all feeds: a recording's feed can sit on another day or court.
  const rowFeed = selectedRow?.feedId
    ? feeds.feeds.find((f) => f.id === selectedRow.feedId)
    : undefined;
  // A row that is now live takes the player to the live feed: drop any pick and held replay so a
  // later finish never falls back to the old replay (adjusting state during render).
  if (selectedRow?.kind === 'live' && pick !== null) {
    setPicks((prev) => ({ ...prev, [key]: null }));
    setHeld((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }
  const heldPlay = selectedRow?.kind === 'link' ? held[key] : undefined;
  const heldFeed = heldPlay ? feeds.feeds.find((f) => f.id === heldPlay.feedId) : undefined;
  const chosen = heldFeed ?? rowFeed ?? cameraFeed;
  const rowFeedLive = rowFeed?.source === 'app' && rowFeed.appState === 'live';
  // Only a replay seeks; a `live` row stays at the live edge with no `start=`.
  const startSec = heldFeed
    ? heldPlay!.startSec
    : rowFeed
      ? selectedRow!.kind === 'video'
        ? playStart(selectedRow!.offsetSec, rowFeedLive)
        : selectedRow!.offsetSec
      : 0;
  const courtName = courtId ? courtLabel(ctx, courtId) : '';

  const select = (row: WatchRow<PublicMatch>) => {
    // A pick pins the court: another court going live must not move this viewer.
    if (courtId) setPickedCourt(courtId);
    // A link row only marks the match: the player stays on what was playing.
    if (row.kind === 'link' && selectedRow?.kind !== 'link' && chosen) {
      setHeld((prev) => ({ ...prev, [key]: { feedId: chosen.id, startSec } }));
    }
    setPicks((prev) => ({ ...prev, [key]: row.kind === 'live' ? null : row.match.id }));
  };
  const pickCamera = (id: string) => {
    if (courtId) setPickedCourt(courtId);
    setCameras((prev) => ({ ...prev, [key]: id }));
    setPicks((prev) => ({ ...prev, [key]: '' }));
  };

  const replaceParams = (mutate: (p: URLSearchParams) => void) => {
    const params = new URLSearchParams(searchParams.toString());
    mutate(params);
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  };
  const setDay = (d: string) => replaceParams((p) => p.set('day', d));

  const platform = chosen ? platformName(chosen.platform, chosen.url) : '';
  const openHref = chosen ? safeWatchUrl(seekUrl(chosen.url, startSec)) : null;
  const outbound = chosen?.display === 'inline' ? chosen : null;
  // The outbound links: the platform, then the feed's other destinations. They sit on the panel's
  // names line, or alone when no row is selected.
  const feedLink = outbound ? (
    <>
      {openHref && (
        <a
          href={openHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('openOn', { platform })}
          className="text-muted self-start text-sm underline"
        >
          {platform}
          <span aria-hidden="true"> &#8599;</span>
        </a>
      )}
      <FeedLinks feed={outbound} includePrimary={false} />
    </>
  ) : null;
  // A tap brings the card the panel describes into view (on a live court with none selected that is
  // the match that just ended); nothing scrolls on load.
  const showSelected = () => {
    const id = shownRow?.match.id;
    if (!id) return;
    Array.from(document.querySelectorAll('[data-match-row]'))
      .find((el) => el.getAttribute('data-match-row') === id)
      ?.scrollIntoView({ block: 'center' });
  };

  return (
    <div className="flex flex-col gap-4 pb-6">
      <div data-testid="watch-player" className="flex flex-col gap-2 py-2">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            {courts.length > 1 ? (
              <ChipRow label={t('courts')} activeKey={courtId ?? ''} className="min-w-0">
                {courts.map((c) => {
                  const liveHere = courtFeeds(c.id).some((f) => statusOf(f) === 'live');
                  return (
                    <Chip
                      key={c.id}
                      preserveCase
                      selected={c.id === courtId}
                      onClick={() => setPickedCourt(c.id)}
                    >
                      {liveHere && (
                        <span
                          data-live-dot
                          aria-hidden="true"
                          className="bg-danger size-1.5 rounded-full"
                        />
                      )}
                      {courtLabel(ctx, c.id)}
                    </Chip>
                  );
                })}
              </ChipRow>
            ) : (
              courtId && <h2 className="text-foreground text-lg font-semibold">{courtName}</h2>
            )}
            {chosen &&
              startSec === 0 &&
              selectedRow?.kind !== 'video' &&
              statusOf(chosen) === 'live' && <LiveBadge />}
          </div>
          {feeds.days.length > 1 && (
            <ChipRow label={t('days')} activeKey={dayKey} className="min-w-0">
              {feeds.days.map((d) => (
                <Chip key={d} selected={d === dayKey} onClick={() => setDay(d)}>
                  <DayChipLabel dayKey={d} locale={locale} />
                </Chip>
              ))}
            </ChipRow>
          )}
        </div>
        {/* Phones: one column, the list follows the video. lg and up: video about 2/3, list about 1/3 (never under 20rem); the player column stays in view while the list flows with the page. */}
        <div className="flex flex-col gap-3 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
          {/* `contents` on phones keeps the video a flex child of the grid, so it sticks across the whole list. */}
          <div
            data-testid="watch-player-column"
            className="contents lg:sticky lg:top-4 lg:col-start-1 lg:row-start-1 lg:flex lg:max-h-[calc(100dvh-5rem)] lg:flex-col lg:gap-3 lg:self-start"
          >
            <div
              data-testid="watch-video"
              className="bg-background sticky top-0 z-10 lg:static lg:mx-auto lg:w-full lg:max-w-[calc((100dvh-15rem)*16/9)] lg:shrink-0 landscape:static"
            >
              {chosen ? (
                <StreamPlayer
                  key={`${chosen.id}:${startSec}`}
                  feed={chosen}
                  title={courtName}
                  compact
                  links="none"
                  startSec={startSec}
                />
              ) : (
                <div className="bg-card border-border text-muted-foreground flex aspect-video w-full items-center justify-center rounded-lg border text-sm">
                  {t('noCameraToday')}
                </div>
              )}
            </div>
            {linkFeeds.length > 1 && (
              <div
                role="group"
                aria-label={t('cameras')}
                className="flex flex-wrap gap-2 lg:shrink-0"
              >
                {linkFeeds.map((feed, i) => (
                  <Chip
                    key={feed.id}
                    preserveCase
                    selected={!rowFeed && feed.id === chosen?.id}
                    onClick={() => pickCamera(feed.id)}
                  >
                    {feed.cameraName ?? t('cameraN', { n: i + 1 })}
                  </Chip>
                ))}
              </div>
            )}
            {shownRow ? (
              <WatchScorecardPanel
                key={shownRow.match.matchId ?? shownRow.match.id}
                row={shownRow}
                onShowCard={showSelected}
                feedLink={feedLink}
              />
            ) : (
              feedLink && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">{feedLink}</div>
              )
            )}
          </div>
          <WatchMatchList
            rows={rows}
            scheduleFailed={scheduleFailed}
            selectedId={selectedRow?.match.id ?? null}
            onSelect={select}
            dayLabel={formatDayKey(dayKey, locale)}
            courtName={courtName}
            locale={locale}
          />
        </div>
      </div>
    </div>
  );
}
